import React, { useEffect, useState } from 'react';
import {
    FulfillmentProductConfiguration,
    PRODUCT_CATEGORIES,
    PRODUCT_CATEGORY,
    PRODUCT_GROUP_TYPE,
    PRODUCT_GROUP_TYPES,
} from '@schuettflix/interfaces';
import { useTranslation } from 'react-i18next';
import { useOrganizationSuspenseQuery } from '@/clients/organization/useOrganization.ts';
import {
    getProductSectionName,
    OrderFulfillmentProduct,
    useFulfillmentContext,
} from '@/modules/fulfillment/context/FulfillmentContext.tsx';
import { FulfillmentCustomRequestProduct } from '@/CustomRequestProductChannel/modules/fulfillment/FulfillmentCustomRequestProduct.tsx';
import { useSetSectionsValidity } from '@/modules/fulfillment/hooks/useSectionsValidity.ts';
import {
    BoxedGroup,
    Button,
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@schuettflix/planum-react';
import { useFranco } from '@/modules/fulfillment/hooks/useFranco.ts';
import { FulfillmentCustomRequestProductForOrderedProduct } from '@/CustomRequestProductChannel/modules/fulfillment/FulfillmentCustomRequestProductForOrderedProduct.tsx';
import { AddProductButton } from '@/shared/components/AddProductButton/AddProductButton.tsx';
import { ProductGroupHeader } from '@/shared/components/ProductGroupHeader/ProductGroupHeader.tsx';
import { MessageBox, ToggleSwitch } from '@schuettflix/react-components';
import { ProductSelectionIndividualLineItemListing } from './ProductSelectionIndividualLineItemListing';
import { useEmitter } from '@/shared/hooks/useEmitter';
import { asyncDelay } from '@/shared/utils/async';
import { MaybePromise } from '@/shared/types/MaybePromise';
import { ProductFallbackDataMap } from '../types/ProductFallbackDataMap';
import { useMarketContext } from '@/shared/context/MarketContext.tsx';
import { useFulfillmentMutations } from '../hooks/useFulfillmentMutations';
import { InlineLoadingSuspenseBoundary } from '@/shared/components/InlineLoadingSuspenseBoundary';

interface ProductSelectionSectionProps {
    disabled?: boolean;
}

export const ProductSelectionSection: React.FC<ProductSelectionSectionProps> = ({ disabled }) => {
    const { marketCode } = useMarketContext();
    const { order, productGroup, orderFulfillmentProducts, fulfillmentProducts, setCanSubmit, contextSource } =
        useFulfillmentContext();
    const { data: orderingOrganization } = useOrganizationSuspenseQuery(order?.customerInfo?.organizationId);

    const isFrancoCase = productGroup.type == PRODUCT_GROUP_TYPES.FRANCO;
    const isMerchantCase = productGroup.type == PRODUCT_GROUP_TYPES.MERCHANT;

    const { t } = useTranslation();
    const [deleteModal, setDeleteModal] = useState<
        { open: false } | { open: true; quoteId?: string | null; index?: number | null; productId?: string | null }
    >({ open: false });
    const setSectionsValidity = useSetSectionsValidity();

    const {
        deleteFulfillmentProductMutation: {
            mutateAsync: mutateAsyncDeleteFulfillmentProduct,
            isPending: deleteFulfillmentProductIsPending,
        },
        deleteFulfillmentQuoteMutation: {
            mutateAsync: mutateAsyncDeleteFulfillmentQuote,
            isPending: deleteFulfillmentQuoteIsPending,
        },
        postFulfillmentProductMutation: { mutateAsync: mutateAsyncPostFulfillmentProduct },
        patchFulfillmentProductConfigurationMutation: { mutateAsync: mutateAsyncPatchFulfillmentProductConfiguration },
    } = useFulfillmentMutations();

    const getFrancoProductPerCategory = (category: PRODUCT_CATEGORY) => {
        if (!isFrancoCase) {
            return;
        }

        return orderFulfillmentProducts.find(product => product.category === category);
    };

    const francoMaterialQuote = getFrancoProductPerCategory(PRODUCT_CATEGORIES.MATERIAL)?.quote;
    const francoTransportQuote = getFrancoProductPerCategory(PRODUCT_CATEGORIES.TRANSPORT)?.quote;

    const { francoFormErrors, onSalesPriceChange } = useFranco({
        initialMaterialSalesPrice: francoMaterialQuote?.salesPrice.toNumber(),
        initialTransportSalesPrice: francoTransportQuote?.salesPrice.toNumber(),
        initialFrancoFormErrors: {},
    });

    const francoMaterialProductFormErrors = {
        ...(francoFormErrors.materialSalesPrice && { salesPrice: francoFormErrors.materialSalesPrice }),
    };

    const francoTransportProductFormErrors = {
        ...(francoFormErrors.transportSalesPrice && { salesPrice: francoFormErrors.transportSalesPrice }),
    };

    const getHintByProduct = (product: OrderFulfillmentProduct) => {
        if (isFrancoCase || isMerchantCase) {
            return product.category === 'MATERIAL' || product.category === 'TRANSPORT'
                ? t('product.fulfillment.productSelection.productIsFrancoHint')
                : t('product.fulfillment.productSelection.productIsNotFrancoHint');
        }
    };

    const getFrancoOverrideFormErrors = (productCategory: PRODUCT_CATEGORY) => {
        if (!isFrancoCase) {
            return;
        }

        if (productCategory === PRODUCT_CATEGORIES.MATERIAL) {
            return francoMaterialProductFormErrors;
        }

        if (productCategory === PRODUCT_CATEGORIES.TRANSPORT) {
            return francoTransportProductFormErrors;
        }
    };

    const handleFrancoSalesPriceChange = (salesPrice: number, productCategory: PRODUCT_CATEGORY) => {
        if (!isFrancoCase) {
            return;
        }

        if (productCategory === PRODUCT_CATEGORIES.MATERIAL) {
            onSalesPriceChange(salesPrice, 'materialSalesPrice');
        }

        if (productCategory === PRODUCT_CATEGORIES.TRANSPORT) {
            onSalesPriceChange(salesPrice, 'transportSalesPrice');
        }
    };

    const handleUpdateFulfillmentProductConfiguration = async (
        fulfillmentProductId: string,
        fulfillmentProductConfiguration: Partial<Omit<FulfillmentProductConfiguration, 'fulfillmentProductId'>> | null
    ) => {
        if (!fulfillmentProductConfiguration || contextSource === 'position') return;

        await mutateAsyncPatchFulfillmentProductConfiguration({
            fulfillmentProductId,
            fulfillmentProductConfiguration,
        });
    };

    const handleDeleteProduct = async (productId: string) => {
        await mutateAsyncDeleteFulfillmentProduct({
            fulfillmentProductId: productId,
        });

        setDeleteModal({ open: false });

        const invalidFulfillmentProducts = fulfillmentProducts
            .filter(product => !product.quote)
            .filter(product => product.id !== productId);

        setCanSubmit(invalidFulfillmentProducts.length === 0);
    };

    //////////////////////////////////
    // Individual Line Item Listing //
    //////////////////////////////////

    // state management for the toggle //
    const [isIndividualLineItemListingActive, setIsIndividualLineItemListingActive] = useState(false);
    const toggleIndividualLineItemListing = () => {
        setIsIndividualLineItemListingDirty(false);
        setIsIndividualLineItemListingActive(v => !v);
    };

    // dirty state management //
    const [isIndividualLineItemListingDirty, setIsIndividualLineItemListingDirty] = useState(false);

    // product name fallback state management //
    // newly created products might not have a quote yet.
    // since we need some of the user input from the product for the individual line item listing,
    // we need to store that data a separate state as a fallback.
    const [productFallbackDataMap, setProductFallbackDataMap] = useState<ProductFallbackDataMap>({});

    // reset forms on toggle //
    const resetEmitter = useEmitter<'preset' | 'zero'>();
    useEffect(() => {
        resetEmitter.emit(isIndividualLineItemListingActive ? 'zero' : 'preset');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isIndividualLineItemListingActive]);

    // modal state //
    const [individualLineItemListingModal, setIndividualLineItemListingModal] = useState<
        { isOpen: false; partToBeCleared?: undefined } | { isOpen: true }
    >({ isOpen: false });
    const checkDirtyAndToggleIndividualLineItemListing = async (shouldSwitchToLineItemListing: boolean) => {
        // wait for the next event loop, since some fields only send updates on blur
        // and we need to make sure they are updated before checking if they are dirty
        // see e.g. https://react-spectrum.adobe.com/react-spectrum/NumberField.html#events
        await asyncDelay(0);

        if (!shouldSwitchToLineItemListing && isIndividualLineItemListingDirty) {
            setIndividualLineItemListingModal({ isOpen: true });

            return;
        }

        toggleIndividualLineItemListing();
    };

    // modal content //
    const renderModalContent = ({
        heading,
        content,
        declineButtonLabel,
        confirmButtonLabel,
        onConfirm,
        isLoading,
    }: {
        heading: string;
        content: string;
        declineButtonLabel: string;
        confirmButtonLabel: string;
        onConfirm: () => MaybePromise<unknown>;
        isLoading?: boolean;
    }) => (
        <DialogContent size="sm">
            <DialogHeader>
                <DialogTitle>{heading}</DialogTitle>
            </DialogHeader>
            <DialogBody>{content}</DialogBody>
            <DialogFooter>
                <BoxedGroup>
                    <Button
                        variant="primary"
                        loading={isLoading}
                        onClick={async () => {
                            await onConfirm();
                        }}
                    >
                        {confirmButtonLabel}
                    </Button>
                    <DialogClose asChild>
                        <Button variant="secondary">{declineButtonLabel}</Button>
                    </DialogClose>
                </BoxedGroup>
            </DialogFooter>
        </DialogContent>
    );

    if (!orderFulfillmentProducts || !orderingOrganization) {
        return null;
    }

    return (
        <>
            {marketCode === 'DE' && (
                <div className="mb-6 flex justify-center">
                    <MessageBox className="w-[720px]">
                        {t(`sections.productGroupSelection.legalCertificateHint`)}
                    </MessageBox>
                </div>
            )}
            <div className="rounded bg-white [counter-reset:productCounter]">
                <div className="border-b p-4">
                    <Header groupType={productGroup.type} />
                </div>
                <div className="divide-y">
                    {orderFulfillmentProducts.map(product => {
                        const disabledMerchantTransport =
                            productGroup.type === 'MERCHANT' && product.category === PRODUCT_CATEGORIES.TRANSPORT;
                        return (
                            <FulfillmentCustomRequestProductForOrderedProduct
                                key={product.id}
                                product={product}
                                orderingOrganization={orderingOrganization}
                                hint={getHintByProduct(product)}
                                groupType={productGroup.type}
                                disabled={disabled}
                                disabledFields={{
                                    unit: disabledMerchantTransport,
                                    amount: disabledMerchantTransport || isIndividualLineItemListingActive,
                                    partnerOrganizationId: disabledMerchantTransport,
                                    salesPrice: disabledMerchantTransport,
                                    salesTaxClassId: disabledMerchantTransport,
                                    purchasePrice: disabledMerchantTransport,
                                    purchaseTaxClassId: disabledMerchantTransport,
                                    serviceDate: disabledMerchantTransport || isIndividualLineItemListingActive,
                                    productName: disabledMerchantTransport,
                                }}
                                disableZeroPriceHints={disabledMerchantTransport}
                                overrideFormErrors={getFrancoOverrideFormErrors(product.category)}
                                positionProductId={product.positionProductId}
                                onSalesPriceChange={salesPrice =>
                                    handleFrancoSalesPriceChange(salesPrice, product.category)
                                }
                                productConfiguration={product.fulfillmentProductConfiguration}
                                onUpdateProductConfiguration={productConfiguration =>
                                    handleUpdateFulfillmentProductConfiguration(product.id, productConfiguration)
                                }
                                onIsDirtyChange={() => {}}
                                resetEmitter={resetEmitter}
                            />
                        );
                    })}
                    {fulfillmentProducts.map(product => (
                        <InlineLoadingSuspenseBoundary key={product.id}>
                            <div className="px-4 py-8">
                                <FulfillmentCustomRequestProduct
                                    key={product.id}
                                    product={{
                                        ...product,
                                        orderProductGroupId: productGroup.id,
                                        positionProductId: null,
                                        index: 0,
                                        productConfiguration: product.fulfillmentProductConfiguration,
                                    }}
                                    quote={
                                        product.quote && {
                                            ...product.quote,
                                            purchasePrice: product.quote.purchasePrice.toNumber(),
                                            salesPrice: product.quote.salesPrice.toNumber(),
                                        }
                                    }
                                    templateId={productGroup.templateId}
                                    showValidation={true}
                                    enableDelete={!disabled}
                                    disabled={disabled}
                                    showProductPriceNotAddedHint={productGroup.type === PRODUCT_GROUP_TYPES.FRANCO}
                                    onCompleteChange={(productId, value) => {
                                        setSectionsValidity(getProductSectionName(productId), value);
                                    }}
                                    onFormChange={form => {
                                        setProductFallbackDataMap(r => ({
                                            ...r,
                                            [form.productId]: {
                                                productName: form.values.productName ?? r[form.productId]?.productName,
                                                unit: form.values.unit ?? r[form.productId]?.unit,
                                                partnerId:
                                                    form.values.partnerOrganizationId ?? r[form.productId]?.partnerId,
                                            },
                                        }));
                                    }}
                                    onDeleteProduct={() => {
                                        setDeleteModal({
                                            open: true,
                                            quoteId: product.quote?.id,
                                            productId: product.id,
                                        });
                                    }}
                                    onUpdateProductConfiguration={fulfillmentProductConfiguration =>
                                        handleUpdateFulfillmentProductConfiguration(
                                            product.id,
                                            fulfillmentProductConfiguration
                                        )
                                    }
                                    onIsDirtyChange={() => {}}
                                    disabledFields={{
                                        amount: isIndividualLineItemListingActive,
                                        serviceDate: isIndividualLineItemListingActive,
                                    }}
                                    resetEmitter={resetEmitter}
                                />
                            </div>
                        </InlineLoadingSuspenseBoundary>
                    ))}
                </div>

                {!disabled && (
                    <div className="flex w-full items-center border-t px-4">
                        <AddProductButton
                            templateId={productGroup.templateId}
                            onAddProduct={async product =>
                                await mutateAsyncPostFulfillmentProduct({
                                    productCategory: product.category,
                                    productType: product.type,
                                    productGroupId: productGroup.id,
                                    index: orderFulfillmentProducts.length + fulfillmentProducts.length + 1,
                                })
                            }
                        />
                        <div className="h-[52px] p-4">
                            <ToggleSwitch
                                label={t('sections.productGroupSelection.individualLineItemListing.toggleLabel')}
                                size="sm"
                                checked={isIndividualLineItemListingActive}
                                onChange={shouldSwitchToLineItemListing => {
                                    void checkDirtyAndToggleIndividualLineItemListing(shouldSwitchToLineItemListing);
                                }}
                            />
                        </div>
                    </div>
                )}
            </div>
            {isIndividualLineItemListingActive && (
                <ProductSelectionIndividualLineItemListing
                    onIsDirtyChange={setIsIndividualLineItemListingDirty}
                    productFallbackDataMap={productFallbackDataMap}
                />
            )}
            <Dialog
                open={individualLineItemListingModal.isOpen}
                onOpenChange={isOpen => !isOpen && setIndividualLineItemListingModal({ isOpen })}
            >
                {renderModalContent({
                    heading: t('sections.productGroupSelection.individualLineItemListing.resetLineItemsModal.heading'),
                    content: t('sections.productGroupSelection.individualLineItemListing.resetLineItemsModal.content'),
                    declineButtonLabel: t(
                        'sections.productGroupSelection.individualLineItemListing.resetLineItemsModal.declineButton'
                    ),
                    confirmButtonLabel: t(
                        'sections.productGroupSelection.individualLineItemListing.resetLineItemsModal.confirmButton'
                    ),
                    onConfirm: () => {
                        toggleIndividualLineItemListing();
                        setIndividualLineItemListingModal({ isOpen: false });
                    },
                })}
            </Dialog>
            <Dialog open={deleteModal.open} onOpenChange={open => setDeleteModal({ open })}>
                {renderModalContent({
                    heading: t('sections.productGroupSelection.askProductDeletion'),
                    content: t('sections.productGroupSelection.productDeletionHint'),
                    declineButtonLabel: t('sections.productGroupSelection.cancelProductDeletion'),
                    confirmButtonLabel: t('sections.productGroupSelection.confirmProductDeletion'),
                    isLoading: deleteFulfillmentProductIsPending || deleteFulfillmentQuoteIsPending,
                    onConfirm: async () => {
                        if (!deleteModal.open) {
                            return;
                        }

                        if (deleteModal.quoteId) {
                            await mutateAsyncDeleteFulfillmentQuote({
                                quoteId: deleteModal.quoteId,
                            });
                            setDeleteModal({ open: false });
                        }

                        if (deleteModal.productId) {
                            await handleDeleteProduct(deleteModal.productId);
                            setSectionsValidity(getProductSectionName(deleteModal.productId), true);
                        }

                        if (deleteModal.index !== undefined) {
                            setDeleteModal({ open: false });
                        }
                    },
                })}
            </Dialog>
        </>
    );
};

interface HeaderProps {
    groupType: PRODUCT_GROUP_TYPE;
}

const Header: React.FC<HeaderProps> = ({ groupType }) => {
    const { products } = useFulfillmentContext();

    return (
        <ProductGroupHeader
            products={products.map(product => ({
                category: product.category,
                amount: product.quote?.amount ?? 0,
                currencyCode: product.quote?.currencyCode ?? 'EUR',
                salesPrice: product.quote?.salesPrice.toNumber() ?? 0,
                purchasePrice: product.quote?.purchasePrice.toNumber() ?? 0,
                unit: product.quote?.unit ?? 'TON',
            }))}
            productGroupType={groupType}
        />
    );
};
